import { authProviderPathMap, EAuthProvider, IUserDto } from "interfaces";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "contexts/AppContext";
import router from "next/router";
import { useCallback, useMemo } from "react";

export default function useCurrentUser() {
  const { apiService } = useAppContext();
  return useQuery<IUserDto | null>({
    queryKey: ["user/me"],
    queryFn: async () => {
      const data = await apiService.get<{ user: IUserDto | null }>("/user/me");

      return data.user;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 5_000,
    retry: false,
    structuralSharing: (oldData, newData) => newData,
    notifyOnChangeProps: ["data", "isFetching", "refetch"],
  });
}

export function useUserClientActions() {
  const queryClient = useQueryClient();
  const { apiService, profile, accessURL, isProfileLoading } = useAppContext();

  const login = useCallback(
    async (p: EAuthProvider) => {
      try {
        const path = authProviderPathMap[p];
        const url = accessURL(path) as URL;
        router.push(url.href);
      } catch (error) {
        console.error({ error });
      }
    },
    [accessURL]
  );

  const isLoadingUserInfo = useMemo(
    () => apiService.getCanRefresh() && !profile && isProfileLoading,
    [apiService, isProfileLoading, profile]
  );
  const isNotLoggedIn = useMemo(() => !isLoadingUserInfo && !profile, [isLoadingUserInfo, profile]);

  const logout = useCallback(() => {
    queryClient.setQueryData<IUserDto | null>(["user/me"], null);
    queryClient.invalidateQueries({ queryKey: ["slot-games"], refetchType: "none" });
    apiService.setCanRefresh(false);
  }, [apiService, queryClient]);

  return { login, logout, isLoadingUserInfo, isNotLoggedIn };
}
